/**
 * Discount Promotion Modal - Bundle Builder Style
 * Handles the discount announcement bar and animated bundle modal
 */

class DiscountPromotion {
  constructor() {
    this.modal = document.getElementById('discount-modal');
    if (!this.modal) return;

    this.bundleProducts = document.getElementById('bundle-products');
    this.bundleSavings = document.getElementById('bundle-savings');
    this.bundleDiscountPercent = document.getElementById('bundle-discount-percent');
    this.bundleSavingsDisplay = document.getElementById('bundle-savings-display');
    
    this.isAnimating = false;
    this.products = [];
    this.productCards = [];
    this.productData = []; // Store original prices
    this.runningSubtotal = 0;
    this.currentDiscount = 0;

    // All discount tiers
    this.tiers = [
      { items: 2, discount: 5 },
      { items: 4, discount: 10 },
      { items: 6, discount: 15 },
      { items: 8, discount: 20 },
      { items: 11, discount: 25 },
      { items: 15, discount: 30 },
      { items: 25, discount: 40 }
    ];

    this.init();
  }

  init() {
    this.loadProductData();
    this.attachEventListeners();
  }

  attachEventListeners() {
    // Open modal from announcement bar
    const openButtons = document.querySelectorAll('[data-action="open-discount-modal"]');
    openButtons.forEach(button => {
      button.addEventListener('click', () => this.openModal());
    });

    // Close modal buttons
    const closeButtons = this.modal.querySelectorAll('[data-action="close-modal"]');
    closeButtons.forEach(button => {
      button.addEventListener('click', () => this.closeModal());
    });

    // ESC key to close
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.modal.classList.contains('is-open')) {
        this.closeModal();
      }
    });
  }

  openModal() {
    this.modal.classList.add('is-open');
    document.body.classList.add('discount-modal-open');
    
    // Save the element that triggered the modal for focus restoration
    this.previouslyFocusedElement = document.activeElement;
    
    // Trigger fade in
    setTimeout(() => {
      this.modal.classList.add('is-visible');
    }, 10);

    // Start animation after modal is visible
    setTimeout(() => {
      this.startAnimation();
    }, 400);

    // Focus management for accessibility
    const ctaButton = this.modal.querySelector('[data-action="close-modal"]');
    if (ctaButton) ctaButton.focus();
    
    // Trap focus within modal
    this.trapFocus();
  }

  closeModal() {
    this.modal.classList.remove('is-visible');
    
    // Stop animation loop
    this.isAnimating = false;
    
    setTimeout(() => {
      this.modal.classList.remove('is-open');
      document.body.classList.remove('discount-modal-open');
      this.resetAnimation();
      
      // Restore focus to the element that opened the modal
      if (this.previouslyFocusedElement) {
        this.previouslyFocusedElement.focus();
      }
    }, 300);
  }

  loadProductData() {
    const productData = document.getElementById('product-data');
    if (!productData) return;

    const productElements = productData.querySelectorAll('[data-product-image]');
    this.products = Array.from(productElements).map(el => ({
      image: el.getAttribute('data-product-image'),
      title: el.getAttribute('data-product-title'),
      price: parseFloat(el.getAttribute('data-product-price')),
      priceCents: parseInt(el.getAttribute('data-product-price-cents'))
    })).filter(p => p.image && p.title && p.price);

    // Preload images
    this.products.forEach(product => {
      const img = new Image();
      img.src = product.image;
    });
  }

  async startAnimation() {
    if (this.isAnimating || !this.bundleProducts) return;
    this.isAnimating = true;

    // Infinite loop
    while (this.isAnimating) {
      // Reset everything
      this.resetAnimation();

      // Add first product without discount
      let productIndex = 0;
      const firstProduct = this.products[productIndex % this.products.length];
      if (firstProduct) {
        this.runningSubtotal += firstProduct.price;
        this.productData.push({
          originalPrice: firstProduct.price,
          card: null
        });
        await this.addProductCard(firstProduct, productIndex);
        productIndex++;
      }

      // Animate through discount tiers starting from tier 1
      for (const tier of this.tiers) {
        if (!this.isAnimating) break;

        await this.sleep(1000);

        // Get product data
        const product = this.products[productIndex % this.products.length];
        if (!product) continue;

        // Add product to running subtotal
        this.runningSubtotal += product.price;

        // Store product data with original price
        this.productData.push({
          originalPrice: product.price,
          card: null
        });

        // Create and add product card
        await this.addProductCard(product, productIndex);

        // Calculate and update totals + prices (now with discount)
        await this.sleep(300);
        this.currentDiscount = tier.discount;
        this.updateTotals(tier.discount);
        this.updateAllProductPrices(tier.discount);

        // Fade older items if too many
        this.manageProductFading();
        
        productIndex++;
      }

      // Pause at final tier
      await this.sleep(2000);

      // Fade out everything
      await this.fadeOutAll();
      
      // Small delay before restart
      await this.sleep(500);
    }
  }

  async addProductCard(product, productIndex) {
    // Get existing cards before adding new one
    const existingCards = Array.from(this.bundleProducts.children);
    const cardHeight = 70; // Approximate height including gap
    
    // Create product card
    const card = document.createElement('div');
    card.className = 'bundle-product-card';
    card.dataset.productIndex = productIndex;
    
    const discountedPrice = product.price * (1 - this.currentDiscount / 100);
    
    card.innerHTML = `
      <img src="${product.image}" alt="${product.title}" class="bundle-product-card__image">
      <div class="bundle-product-card__details">
        <div class="bundle-product-card__title">${product.title}</div>
        <div class="bundle-product-card__price" data-original-price="${product.price}">$${discountedPrice.toFixed(2)}</div>
      </div>
    `;

    // Store card reference in productData
    this.productData[productIndex].card = card;

    // Step 1: Disable transitions on existing cards
    existingCards.forEach(existingCard => {
      existingCard.style.transition = 'none';
    });

    // Step 2: Move existing cards up by card height (to counteract the upcoming DOM shift)
    existingCards.forEach(existingCard => {
      existingCard.style.transform = `translateY(-${cardHeight}px)`;
    });

    // Step 3: Add new card to TOP (this will shift DOM, but transform keeps visual position)
    this.bundleProducts.insertBefore(card, this.bundleProducts.firstChild);
    this.productCards.unshift(card);

    // Step 4: Force reflow to apply the transform
    void this.bundleProducts.offsetHeight;

    // Step 5: Re-enable transitions
    existingCards.forEach(existingCard => {
      existingCard.style.transition = 'transform 0.4s ease-out, opacity 0.4s ease';
    });

    // Step 6: Animate existing cards back to 0 (they smoothly slide down now)
    await this.sleep(10);
    existingCards.forEach(existingCard => {
      existingCard.style.transform = 'translateY(0)';
    });

    // Step 7: Slide in new card from left
    await this.sleep(50);
    card.classList.add('active');
  }

  manageProductFading() {
    // Fade older items at the bottom if we have more than 4 visible
    const visibleCount = 4;
    if (this.productCards.length > visibleCount) {
      // Since newest are at top (index 0), fade items at the end of array
      const itemsToFade = this.productCards.length - visibleCount;
      for (let i = this.productCards.length - 1; i >= this.productCards.length - itemsToFade; i--) {
        if (this.productCards[i]) {
          this.productCards[i].classList.add('fading');
        }
      }
    }
  }

  updateTotals(discountPercent) {
    if (discountPercent > 0) {
      const discountMultiplier = 1 - (discountPercent / 100);
      const discountedTotal = this.runningSubtotal * discountMultiplier;
      const savingsAmount = this.runningSubtotal - discountedTotal;

      // Update savings amount
      if (this.bundleSavings) {
        const currentSavings = parseFloat(this.bundleSavings.textContent.replace('$', '')) || 0;
        this.animateValue(this.bundleSavings, currentSavings, savingsAmount, 300);
      }

      // Update discount percentage and activate green styling
      if (this.bundleDiscountPercent) {
        this.bundleDiscountPercent.textContent = `${discountPercent}% off`;
        this.bundleDiscountPercent.classList.add('active');
      }
    } else {
      // Keep savings at $0.00 and pill at 0% when there's no discount
      if (this.bundleSavings) {
        this.bundleSavings.textContent = '$0.00';
      }
      
      if (this.bundleDiscountPercent) {
        this.bundleDiscountPercent.textContent = '0% off';
        this.bundleDiscountPercent.classList.remove('active');
      }
    }
  }

  updateAllProductPrices(discountPercent) {
    // Update all visible product prices with discounted amount
    this.productData.forEach((data, index) => {
      if (data.card) {
        const priceElement = data.card.querySelector('.bundle-product-card__price');
        if (priceElement) {
          const discountedPrice = data.originalPrice * (1 - discountPercent / 100);
          const currentPrice = parseFloat(priceElement.textContent.replace('$', ''));
          
          // Animate price update
          this.animateValue(priceElement, currentPrice, discountedPrice, 300);
          
          // Add/remove discounted class for green color
          if (discountPercent > 0) {
            priceElement.classList.add('discounted');
          } else {
            priceElement.classList.remove('discounted');
          }
        }
      }
    });
  }

  animateValue(element, start, end, duration) {
    const startTime = performance.now();
    const difference = end - start;

    const updateValue = (currentTime) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      
      // Easing function
      const easeProgress = progress < 0.5
        ? 2 * progress * progress
        : 1 - Math.pow(-2 * progress + 2, 2) / 2;
      
      const currentValue = start + (difference * easeProgress);
      element.textContent = `$${currentValue.toFixed(2)}`;

      if (progress < 1) {
        requestAnimationFrame(updateValue);
      }
    };

    requestAnimationFrame(updateValue);
  }

  async fadeOutAll() {
    // Roll savings back to $0.00
    if (this.bundleSavings) {
      const currentSavings = parseFloat(this.bundleSavings.textContent.replace('$', '')) || 0;
      this.animateValue(this.bundleSavings, currentSavings, 0, 500);
    }

    // Roll discount percentage back to 0% and remove active class
    if (this.bundleDiscountPercent) {
      this.bundleDiscountPercent.textContent = '0% off';
      this.bundleDiscountPercent.classList.remove('active');
    }

    await this.sleep(500);

    // Fade out all product cards (prices stay as-is)
    this.productCards.forEach(card => {
      card.style.transition = 'opacity 0.3s ease';
      card.style.opacity = '0';
    });

    await this.sleep(300);
  }

  resetAnimation() {
    // Clear products list
    if (this.bundleProducts) {
      this.bundleProducts.innerHTML = '';
    }

    // Reset arrays and counters
    this.productCards = [];
    this.productData = [];
    this.runningSubtotal = 0;
    this.currentDiscount = 0;

    // Savings should already be at $0.00 and pill at 0% from fadeOutAll
  }

  sleep(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  trapFocus() {
    const focusableElements = this.modal.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    
    if (focusableElements.length === 0) return;
    
    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];
    
    // Remove previous listener if exists
    if (this.focusTrapHandler) {
      this.modal.removeEventListener('keydown', this.focusTrapHandler);
    }
    
    this.focusTrapHandler = (e) => {
      if (e.key !== 'Tab') return;
      
      if (e.shiftKey) {
        // Shift + Tab
        if (document.activeElement === firstElement) {
          e.preventDefault();
          lastElement.focus();
        }
      } else {
        // Tab
        if (document.activeElement === lastElement) {
          e.preventDefault();
          firstElement.focus();
        }
      }
    };
    
    this.modal.addEventListener('keydown', this.focusTrapHandler);
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new DiscountPromotion();
  });
} else {
  new DiscountPromotion();
}

